/**
 * For now, chat is the only AI feature that is surfaced through the sidepanel, so this file lives in
 * the llm/chat-library folder.
 */
import {getTestProps} from '@benchling/js-test-utils';
import {Tooltip, UnstyledButton} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {
  PromptSuggestionCardGallery,
  SignedOutPromptSuggestionCardGallery,
} from 'llm/chat-library/prompt-suggestions/PromptSuggestionCards';
import {MCPEnabledServersProvider} from 'llm/mcp-client-library/MCPEnabledServersContext';
import {getChatUrlPath} from 'llm/routes/UrlUtils';
import {LOGGED_OUT_MODAL_KEY} from 'nav/LoggedOutModal';
import SharedFileSidepanel from 'nav/share/growth/SharedFileSidepanel';
import {ShareButton} from 'nav/share/ShareBar';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import ToggleDropdown from 'nav/ToggleDropdown';
import {getActiveShareLinkItemId} from 'ShareAccess';
import EntityStore from 'stores/EntityStore';
import {ZINDEX_SIDE_NAVBAR} from 'styles/StyleConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';
import useOnMount from 'ui-library/hooks/useOnMount';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as ModalContainer from 'ui-library/ModalContainer';

import AITrialSignupModal from '../auth/AITrialSignupModal';
import {NewChatInput} from '../ChatInput';
import {AIOnboardingScreen} from './AIOnboardingScreen';
import {HAS_SEEN_AI_SIDEPANEL_ONBOARDING, AISettingsContent} from './AISettings';
import {InitialUI, useAISidepanelGlobalClientState} from './AISidepanelV2GlobalClientState';
import ChatListing from './ChatListing';
import ChatRouter from './ChatRouter';
import type {CreateChatResult, SidepanelChat} from './ChatRouter';
import {doesAgentTypePreferExpandedSidepanel, isAISidepanelEnabled} from './ChatTypeUtil';
import {NoAccessChatListing} from './NoAccessChatListing';
import {restartOnboardingSession, startOnboardingSession} from './OnboardingDAO';
import ReadOnlyChat from './ReadOnlyChat';

const SIDEPANEL_SEMI_EXPANDED_WIDTH = 480;
const HISTORY_DRAWER_WIDTH = 286;

export interface AISidepanelProps {
  isExpanded: boolean;
}

function openTrialSignUpModal(trigger: string): void {
  ModalContainer.open(
    LOGGED_OUT_MODAL_KEY,
    <AITrialSignupModal
      trigger={trigger}
      onRequestClose={() => {
        ModalContainer.close(LOGGED_OUT_MODAL_KEY);
      }}
    />
  );
}

function WelcomeToChatPanel(props: {
  isExpanded: boolean;
  onCreateChat: (result: CreateChatResult) => void;
  isSignedOut: boolean;
}): JSX.Element {
  return (
    <div
      className={css(styles.welcomeContainer)}
      {...getTestProps({
        component: 'WelcomeToChatPanel',
        element: 'root',
      })}
    >
      <div className={css(styles.welcomeHero)}>
        <div className={css(styles.welcomeHeader)}>What can I help with today?</div>
        {!props.isSignedOut && BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT && (
          <div className={css(styles.publicTrialBanner)}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text="You are on a free trial of Benchling AI."
              supportingInfo={
                <div>
                  <p>
                    All data is stored{' '}
                    <a href="https://www.benchling.com/trust" target="_blank" rel="noopener noreferrer">
                      securely
                    </a>
                    , but do not upload sensitive data unless you have permission from your organization.
                  </p>
                  <p>
                    If you are an existing Benchling customer, you can use Benchling AI in your organization's
                    Benchling instance. Contact your administrator for access to Benchling AI.
                  </p>
                </div>
              }
            />
          </div>
        )}
      </div>
      {/* Draft state (including agentType) is initialized by openDraftChat */}
      <NewChatInput onCreateChat={props.onCreateChat} />
      <div className={css(styles.welcomeFooter)}>
        {props.isSignedOut && BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT ? (
          <SignedOutPromptSuggestionCardGallery />
        ) : (
          <PromptSuggestionCardGallery />
        )}
      </div>
    </div>
  );
}

function isActiveShareLink(chatId: string | null): boolean {
  return chatId != null && getActiveShareLinkItemId() === chatId;
}

/**
 * If the sidepanel is expanded, set the URL appropriately.
 */
function useSyncStateToURL(isExpanded: boolean): void {
  const [panelState] = useAISidepanelGlobalClientState();
  const isShareLink = isActiveShareLink(panelState.activeChat.id);
  useEffect(() => {
    // We only control the URL if we are expanded; otherwise, whatever is open in the workspace sets the URL.
    if (isExpanded && !isShareLink) {
      // TODO: rewrite the url to match the share link url

      const newPath = getChatUrlPath(panelState.activeChat.id ?? 'new');

      // Leave the URL alone if the path matches. This ensures that any querystring parameters are preserved,
      // which is important for marketing (GoogleTagManagerForAITrial.ts)
      if (newPath === window.location.pathname) {
        return;
      }
      navigateToURL(newPath, {
        replace: false, // No need to replace: navigateToURL already no-ops if the URL hasn't changed.
        skipTrigger: true, // we are syncing the state to the URL, so no need to trigger the route function
      });
    }
  }, [panelState, isExpanded, isShareLink]);
}

/**
 * When collapsing the sidepanel, auto-hide the history if it is currently toggled on..
 * When expanding the sidepanel, show the history based on the user's last explicit toggle of the history.
 */
function useHistoryAutoHide(isExpanded: boolean): void {
  const [panelState, setPanelState] = useAISidepanelGlobalClientState();

  const prevIsExpanded = useRef(isExpanded);
  useEffect(() => {
    if (prevIsExpanded.current && !isExpanded) {
      setPanelState({...panelState, showHistory: false});
    }
    if (!prevIsExpanded.current && isExpanded) {
      setPanelState({...panelState, showHistory: panelState.showHistoryPreference});
    }
    prevIsExpanded.current = isExpanded;
  }, [isExpanded, setPanelState, panelState]);
}

/**
 * On mobile-size screens, we trigger expand/close events if we are ever in a
 * semi-expanded state.
 */
function useDisallowSemiExpandedOnMobile(isExpanded: boolean, isPhoneWidth: boolean): void {
  const lastIsExpanded = useRef<boolean | null>(null);
  useEffect(() => {
    if (isPhoneWidth && !isExpanded) {
      if (lastIsExpanded.current === true) {
        // If we moved from expanded to semi-expanded, close the sidepanel, unmounting us.
        SidepanelActions.close();
      } else {
        // If we just mounted (i.e. just opened the sidepanel) or switched to phone width
        // while semi-expanded, expand to full view.
        SidepanelActions.expand();
      }
    }
    lastIsExpanded.current = isExpanded;
  }, [isExpanded, isPhoneWidth]);
}

function SignedInHeader({
  isExpanded,
  isWideView,
  isPhoneWidth,
  isShareLink,
  hasChatAccess,
  isSettingsOpen,
  setIsSettingsOpen,
  isOnboarding,
}: {
  isExpanded: boolean;
  isWideView: boolean;
  isPhoneWidth: boolean;
  isShareLink: boolean;
  hasChatAccess: boolean;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (isOpen: boolean) => void;
  isOnboarding: boolean;
}): JSX.Element {
  const [panelState, setPanelState] = useAISidepanelGlobalClientState();

  const handleSettingsClick = useCallback(() => {
    if (!isSettingsOpen) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_AI_SETTINGS_PANEL);
    }
    setIsSettingsOpen(!isSettingsOpen);
  }, [isSettingsOpen, setIsSettingsOpen]);

  const handleCloseAIView = useCallback(() => {
    SidepanelActions.close();
  }, []);

  const onOpenHistory = useCallback(() => {
    setIsSettingsOpen(false);
    // If the user toggles the history, and capture it as their last explicit toggle.
    setPanelState({
      ...panelState,
      showHistory: !panelState.showHistory,
      showHistoryPreference: !panelState.showHistory,
    });
  }, [setPanelState, panelState, setIsSettingsOpen]);

  const onResumeNewChat = useCallback((): void => {
    setIsSettingsOpen(false);
    // If the user is already viewing the welcome panel, focus the chat input.
    if (panelState.activeChat.id == null) {
      // TODO(BNCH-132631): Use a ref to the ChatInput component.
      (document.querySelector('.sidepanel textarea') as HTMLTextAreaElement | undefined)?.focus();
    }
    setPanelState({
      ...panelState,
      activeChat:
        // Reset the active chat if we are returning to the welcome panel.
        panelState.activeChat.id != null ? {id: null} : panelState.activeChat,
      // Only show history if we have the screen real estate.
      showHistory: panelState.showHistoryPreference && isWideView,
    });
  }, [setPanelState, panelState, isWideView, setIsSettingsOpen]);

  return (
    <div className={classNames(css(styles.sidepanelHeader))}>
      <div
        className={classNames(
          css(
            // HACK: Ideally these buttons are part of the drawer; otherwise, the header is not centered.
            // These styles cause the buttons to take up the same space as the drawer, thus pushing
            // the header back to the center.
            styles.leftHeaderButtons,
            panelState.showHistory && isWideView && styles.leftHeaderButtonsExpanded
          )
        )}
      >
        {!isOnboarding && (
          <>
            {/* NB: Tooltip child needs to accept a ref, but our Button doesn't, so wrap in a span. */}
            <Tooltip label="Chat history">
              <span>
                <Button
                  aria-label="Chat history"
                  buttonStyle={ButtonStyle.TERTIARY_WHITE}
                  className={classNames({'is-active': panelState.showHistory})}
                  icon="history"
                  onClick={onOpenHistory}
                  size={ButtonSize.SMALL}
                />
              </span>
            </Tooltip>
            <Tooltip label="New chat" disabled={isWideView}>
              {/* Use u-flex-none to avoid text from wrapping when expanding / collapsing the sidepanel. */}
              <span className="u-flex-none">
                <Button
                  aria-label="New chat"
                  buttonStyle={ButtonStyle.TERTIARY_WHITE}
                  icon="edit"
                  onClick={onResumeNewChat}
                  size={ButtonSize.SMALL}
                  text={isWideView ? 'New chat' : null}
                />
              </span>
            </Tooltip>
          </>
        )}
      </div>
      <div role="heading" aria-level={1} className={css(styles.headerTitle)}>
        <UnstyledButton fz="18px" onClick={onResumeNewChat} tabIndex={0}>
          Benchling AI
        </UnstyledButton>
      </div>
      <div className={css(styles.rightHeaderButtons)}>
        {!isOnboarding &&
          panelState.activeChat.id &&
          // We don't support "resharing" from a shared chat, so don't show the share button.
          !isShareLink && (
            <ShareButton
              fileType={ModelTypeConstants.CHAT_SESSION}
              fileId={panelState.activeChat.id}
              fileName="my chat" // TODO: Use a GraphQL query.
              buttonSize={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.TEXT}
              filePerms={{writable: true}} // TODO: graphql query
            />
          )}
        {!isOnboarding && hasChatAccess && (
          <Tooltip label="Settings" disabled={isSettingsOpen}>
            <span>
              <Button
                aria-label="Settings"
                buttonStyle={ButtonStyle.TERTIARY_WHITE}
                className={classNames({'is-active': isSettingsOpen})}
                icon="gear"
                onClick={handleSettingsClick}
                size={ButtonSize.SMALL}
              />
            </span>
          </Tooltip>
        )}
        {isPhoneWidth ||
        (panelState.activeChat.id != null && panelState.activeChat.type === ChatType.LEGACY_DATA_IMPORT) ? (
          <Button
            icon="close"
            onClick={handleCloseAIView}
            buttonStyle={ButtonStyle.TRANSPARENT}
            size={ButtonSize.SMALL}
            dataTestAttrs={{component: 'AISidepanel', element: 'close-button'}}
          />
        ) : (
          <ToggleDropdown expanded={isExpanded} />
        )}
      </div>
    </div>
  );
}

/**
 * Header for both LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT and for chats accessed via share link.
 */
function SignedOutHeader({isShareLink}: {isShareLink: boolean}): JSX.Element {
  return (
    <div
      className={classNames(
        css(styles.sidepanelHeaderSignedOut, isShareLink && styles.sidepanelHeaderSignedOutShareLink)
      )}
    >
      <div role="heading" aria-level={1} className={css(styles.headerTitleSignedOut)}>
        <BenchlingIcon icon="benchling-logo" className={css(styles.headerTitleLogoSignedOut)} />
        <span className={css(styles.headerTitleTextSignedOut)}>Benchling AI</span>
      </div>
      <div>
        {!isShareLink && (
          <div className={css(styles.headerButtonsSignedOut)}>
            <ButtonLink
              className="u-flex-none"
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.SECONDARY_WHITE}
              href="/signin?next=/chats/new"
            >
              Sign in
            </ButtonLink>
            <ButtonLink
              className="u-flex-none"
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.PRIMARY}
              href="/signup?next=/chats/new"
              onClick={(e) => {
                if (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT) {
                  e.preventDefault();
                  openTrialSignUpModal('signup_button_click');
                }
              }}
            >
              Sign up
            </ButtonLink>
          </div>
        )}
      </div>
    </div>
  );
}

export function AISidepanel(props: AISidepanelProps): JSX.Element {
  const [panelState, setPanelState] = useAISidepanelGlobalClientState();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const refetchRef = useRef<() => void>(() => {});
  const isPhoneWidth = useIsPhoneWidth();
  const isWideView = !isPhoneWidth && props.isExpanded;

  useSyncStateToURL(props.isExpanded);
  useHistoryAutoHide(props.isExpanded);
  useDisallowSemiExpandedOnMobile(props.isExpanded, isPhoneWidth);

  useOnMount(() => {
    const isSignedOut = EntityStore.currentUser() == null;
    trackEvent(
      isSignedOut && BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT
        ? LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_AI_TRIAL_HOMEPAGE
        : LLM_CHAT_ANALYTICS_ACTIONS.OPENED_AI_SIDEPANEL
    );
    switch (panelState.initialModal) {
      case InitialUI.SIGNUP:
        if (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT) {
          openTrialSignUpModal('signup_url');
        }
        break;
      case InitialUI.SETTINGS:
        setIsSettingsOpen(true);
        break;
      default:
        break;
    }
  });

  // NOTE: It's a bit sketchy that our callback is dependent on the current panelState. There is some risk
  // that the component will invoke the callback asynchronously where it will invoke the stale callback.
  // React's setState avoids this by having a callback API: `setState((oldState) => )`, which
  // useGlobalClientState does not support. This will be a bigger issue if there is intentionally other
  // state that the user accumulates while waiting for the chat to be created. In practice, it may not
  // be an issue since creating a chat on the backend is a relatively quick operation.
  const onCreateChat = useCallback(
    (result: CreateChatResult): void => {
      setPanelState({
        ...panelState,
        activeChat: {id: result.chatSessionId, type: result.chatType},
      });
      refetchRef.current();
      if (doesAgentTypePreferExpandedSidepanel(result.initialAgentType)) {
        SidepanelActions.expand();
      }
    },
    [setPanelState, panelState, refetchRef]
  );

  const onOpenChat = useCallback(
    (chat: SidepanelChat) => {
      // If the user clicks on a chat in semi-expanded or mobile, consider that an implicit toggle close of the history.
      const showHistoryPreference = panelState.showHistoryPreference && isWideView;
      setPanelState({
        ...panelState,
        // If a different chat was selected, pick a new client id to reset any component state.
        clientChatId: panelState.activeChat.id === chat.id ? panelState.clientChatId : uuidv4(),
        activeChat: chat,
        showHistoryPreference,
        // Only show history if we have the screen real estate.
        showHistory: showHistoryPreference && isWideView,
      });
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_LLM_CHAT_FROM_HISTORY, {
        chatId: chat.id,
        chatType: chat.id != null ? chat.type : null,
      });
    },
    [setPanelState, panelState, isWideView]
  );

  const isShareLink = isActiveShareLink(panelState.activeChat.id);
  const isSignedOut = EntityStore.currentUser() == null;
  const hasChatAccess = isAISidepanelEnabled();

  const [hasSeenOnboarding, setHasSeenOnboarding] = usePreference<boolean | undefined>(
    HAS_SEEN_AI_SIDEPANEL_ONBOARDING
  );
  const showOnboarding = hasChatAccess && !hasSeenOnboarding && BenchlingConfig.LLM_ENABLE_AI_ONBOARDING;

  // Kick off the onboarding session before AIOnboardingScreen mounts so the
  // backend task starts immediately.
  const [onboardingChatSessionId, setOnboardingChatSessionId] = useState<string | null>(null);
  const onboardingSessionPromise = useRef<Promise<string> | null>(null);

  useEffect(() => {
    if (!showOnboarding) {
      return;
    }
    // Only start if we haven't kicked off the onboarding session for this "showOnboarding" view.
    if (onboardingSessionPromise.current != null) {
      return;
    }
    let cancelled = false;
    const promise = startOnboardingSession().then(({session_id: sessionId}) => {
      if (!cancelled) {
        setOnboardingChatSessionId(sessionId);
      }
      return sessionId;
    });
    onboardingSessionPromise.current = promise;
    return () => {
      cancelled = true;
      // If cleaning up (e.g. showOnboarding just became false), reset the promise for future attempts.
      onboardingSessionPromise.current = null;
    };
  }, [showOnboarding]);

  const handleRestartOnboarding = useCallback(async () => {
    await restartOnboardingSession();
    onboardingSessionPromise.current = null;
    setOnboardingChatSessionId(null);
    setHasSeenOnboarding(false);
    setIsSettingsOpen(false);
  }, [setHasSeenOnboarding, setIsSettingsOpen]);

  /**
   * Here is an ASCII diagram of the layout we intend to achieve:
   *
   *     sidepanel
   *   +-------------------------------------+
   *   | [btns]   Benchling AI        [btns] |  header
   *   +-------------------------------------+
   *   | +----------+----------------------+ |  body
   *   | | drawer   | main content         | |
   *   | | +------+ | +-------+----------+ | |
   *   | | | his- | | | share |  chat    | | |
   *   | | | tory | | | panel |          | | |
   *   | | +------+ | +-------+----------+ | |
   *   | +---------+-----------------------+ |
   *   +-------------------------------------+
   *
   * When semi-expanded, the history drawer covers up the main content.
   * When expanded, the history drawer pushes the main content to the right.
   */

  const sidepanelContent = (
    <div
      className={classNames(
        'sidepanel',
        css(styles.sidepanel, (isSignedOut || isPhoneWidth) && styles.sidepanelCoveringNavBar)
      )}
    >
      {isSignedOut ? (
        <SignedOutHeader isShareLink={isShareLink} />
      ) : (
        <SignedInHeader
          isExpanded={props.isExpanded}
          isWideView={isWideView}
          isPhoneWidth={isPhoneWidth}
          isShareLink={isShareLink}
          hasChatAccess={hasChatAccess}
          isSettingsOpen={isSettingsOpen}
          setIsSettingsOpen={setIsSettingsOpen}
          isOnboarding={showOnboarding}
        />
      )}
      <div className={css(styles.sidepanelBody)}>
        <div
          className={css(
            styles.mainContent,
            panelState.showHistory && isWideView && styles.mainContentShiftedForHistory
          )}
          {...getTestProps({
            component: 'AISidepanelV2',
            element: 'main-content',
          })}
        >
          {isSignedOut && window.bootstrap?.sharedFolderItemAPIId && (
            <div
              className={css(styles.sharedSidepanelContainer)}
              {...getTestProps({
                component: 'AISidepanelV2',
                element: 'sidepanel-container',
              })}
            >
              <SharedFileSidepanel useSidepanelStyling={false} />
            </div>
          )}
          <div className={classNames(css(styles.sidepanelChatContainer), 'scrollbar-override')}>
            {(() => {
              if (showOnboarding) {
                if (!onboardingChatSessionId) {
                  return null;
                }
                return (
                  <AIOnboardingScreen
                    chatSessionId={onboardingChatSessionId}
                    onDismiss={() => {
                      setHasSeenOnboarding(true);
                      setIsSettingsOpen(true);
                    }}
                  />
                );
              }
              if (panelState.activeChat.id == null) {
                if (hasChatAccess) {
                  return (
                    <WelcomeToChatPanel
                      isExpanded={props.isExpanded}
                      onCreateChat={onCreateChat}
                      isSignedOut={isSignedOut}
                    />
                  );
                } else {
                  return (
                    <NoAccessChatListing
                      dataTestAttrs={{component: 'NoAccessChatListing', element: 'root', key: 'chatPanel'}}
                    />
                  );
                }
              } else if (isShareLink) {
                return <ReadOnlyChat chatId={panelState.activeChat.id} isExpanded={props.isExpanded} />;
              } else {
                return (
                  <ChatRouter
                    key={panelState.clientChatId}
                    chat={panelState.activeChat}
                    isExpanded={props.isExpanded}
                  />
                );
              }
            })()}
          </div>
        </div>
        {/* ChatListing is rendered after WelcomeToChatPanel to get a higher z-index.
            CSS will place it to the left. */}
        <div
          className={css(
            styles.drawer,
            !panelState.showHistory && isWideView && styles.closedDrawerOnFullyExpandedSidepanel,
            !panelState.showHistory && !isWideView && styles.closedDrawerOnSemiExpandedSidepanel,
            panelState.showHistory && !isWideView && styles.openDrawerOnSemiExpandedSidepanel,
            panelState.showHistory && isWideView && styles.openDrawerOnFullyExpandedSidepanel
          )}
          {...getTestProps({component: 'AISidepanel', element: 'drawer'})}
        >
          <div className={classNames(css(styles.chatListingContainer), 'scrollbar-override')}>
            {hasChatAccess ? (
              <ChatListing
                selectedChatSession={panelState.activeChat}
                onSelectChatSession={onOpenChat}
                refetchRef={refetchRef}
              />
            ) : (
              <NoAccessChatListing
                dataTestAttrs={{component: 'NoAccessChatListing', element: 'root', key: 'chatListing'}}
              />
            )}
          </div>
        </div>
        {isSettingsOpen && (
          <div
            className={css(
              styles.settingsDrawer,
              props.isExpanded
                ? styles.settingsDrawerOnFullyExpandedSidepanel
                : styles.settingsDrawerOnSemiExpandedSidepanel
            )}
            {...getTestProps({component: 'AISidepanelV2', element: 'settings-overlay'})}
          >
            <AISettingsContent
              chatId={panelState.activeChat.id}
              onRestartOnboarding={
                BenchlingConfig.LLM_ENABLE_AI_ONBOARDING ? handleRestartOnboarding : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  );

  return isSignedOut ? (
    sidepanelContent
  ) : (
    <MCPEnabledServersProvider>{sidepanelContent}</MCPEnabledServersProvider>
  );
}

function transitionValuesForDrawerEffect(properties: Array<string>): string {
  return properties.map((property) => `${property} 0.3s ease-in-out`).join(', ');
}

const PADDING = 16;

const styles = StyleSheet.create({
  headerButtonsSignedOut: {
    display: 'flex',
    gap: '4px',
  },
  welcomeContainer: {
    maxWidth: 650 + PADDING * 2,
    width: '100%',
    padding: `0 ${PADDING}px`,
    margin: '0px auto',
    display: 'flex',
    flexDirection: 'column',
    // aphrodite typing is incomplete. `safe` is implemented by modern browsers, but is ignored and thus
    // gracefully degrades to `center` in older (unsupported) browsers. This ensures that in narrow viewports,
    // center is not used if the content overflows / scrolls.
    justifyContent: 'safe center' as unknown as 'center',
  },
  welcomeHero: {
    textAlign: 'center',
    marginTop: '20px',
    marginBottom: '45px',
  },
  welcomeHeader: {
    fontSize: '22px',
    lineHeight: '26px',
    fontWeight: 400,
  },
  welcomeFooter: {
    marginTop: '20px',
    paddingBottom: '20px',
    // Mild hack to push the ChatInput upwards: `justify-content : safe center` will center all of the
    // content, but the ChatInput may not be totally centered since the footer and header aren't the
    // same height.
    marginBottom: '40px',
  },
  welcomeSectionHeader: {
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '12px',
    marginTop: '45px',
  },
  chatListingContainer: {
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  sidepanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  publicTrialBanner: {
    textAlign: 'left',
    marginTop: 20,
  },
  // In some scenarios (mobile and logged out view), we don't want the navbar to be visible at all,
  // so implement that by just covering it up.
  sidepanelCoveringNavBar: {
    left: 0,
    zIndex: ZINDEX_SIDE_NAVBAR + 1,
    transition: 'none',
  },
  sidepanelHeaderSignedOut: {
    padding: '16px 8px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  sidepanelHeaderSignedOutShareLink: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  sidepanelHeader: {
    padding: '16px 8px 14px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    zIndex: 3,
  },
  leftHeaderButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '0px',
    overflow: 'visible',
    transition: transitionValuesForDrawerEffect(['width']),
    zIndex: 1,
  },
  rightHeaderButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '0px',
    overflow: 'visible',
    zIndex: 1,
    justifyContent: 'flex-end',
  },
  leftHeaderButtonsExpanded: {
    width: HISTORY_DRAWER_WIDTH - 8,
  },
  headerTitle: {
    fontSize: '18px',
    lineHeight: '24px',
    flex: 1,
    textAlign: 'center',
    zIndex: 0,
  },
  headerTitleSignedOut: {
    marginLeft: 16,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 22,
  },
  headerTitleLogoSignedOut: {
    color: '#2B39F1',
    marginRight: 5,
  },
  headerTitleTextSignedOut: {
    background: 'linear-gradient(to right, #000DB5, #9A4BD7)',
    color: 'transparent',
    backgroundClip: 'text',
  },
  sidepanelBody: {
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  sidepanelChatContainer: {
    display: 'flex',
    flex: 1,
    overflowY: 'auto',
    position: 'relative',
    zIndex: 1,
  },
  drawer: {
    backgroundColor: 'white',
    position: 'absolute',
    flex: 'none',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    display: 'flex',
    flexDirection: 'column',
    transition: transitionValuesForDrawerEffect(['width', 'left']),
    zIndex: 2,
    top: 0,
    bottom: 0,
  },
  closedDrawerOnSemiExpandedSidepanel: {
    width: '100%',
    left: '-100%',
  },
  closedDrawerOnFullyExpandedSidepanel: {
    width: HISTORY_DRAWER_WIDTH,
    left: -HISTORY_DRAWER_WIDTH,
  },
  openDrawerOnSemiExpandedSidepanel: {
    width: '100%',
    left: 0,
  },
  openDrawerOnFullyExpandedSidepanel: {
    width: HISTORY_DRAWER_WIDTH,
    left: 0,
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
    position: 'absolute',
    display: 'flex',
    transition: transitionValuesForDrawerEffect(['left']),
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    '@media (min-width: 768px)': {
      // Avoids drawer animation from squishing content when switching sidepanels.
      minWidth: SIDEPANEL_SEMI_EXPANDED_WIDTH,
    },
  },
  mainContentShiftedForHistory: {
    left: HISTORY_DRAWER_WIDTH,
  },
  sharedSidepanelContainer: {
    display: 'flex',
    width: 360,
  },
  settingsDrawer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 8px',
  },
  settingsDrawerOnFullyExpandedSidepanel: {
    width: 400,
    boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.12)',
  },
  settingsDrawerOnSemiExpandedSidepanel: {
    left: 0,
  },
});

export default AISidepanel;
